import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { API, t } = useApp();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API}/auth/verify-email/${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || t('verification_failed'));
        }

        setEmail(data.email);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err.message);
      }
    };

    verifyEmail();
  }, [token, API, t]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12 relative grain"
      style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
    >
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <Link to="/" className="block text-center mb-8">
          <h1 className="font-heading text-3xl">
            <span className="gold-text">Zuiver</span>
            <span className="text-white">Parfum</span>
          </h1>
        </Link>

        {/* Content Card */}
        <div className="glass p-8 rounded-2xl text-center">
          {status === 'verifying' && (
            <>
              <Loader2 size={64} className="mx-auto text-[#D4AF37] animate-spin mb-6" />
              <h2 className="font-heading text-2xl text-white mb-2">
                {t('verifying_email')}
              </h2>
              <p className="text-[#A8A29E]">
                {t('please_wait_verifying')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
              <h2 className="font-heading text-2xl text-white mb-2">
                {t('email_verified')}
              </h2>
              <p className="text-[#A8A29E] mb-6">
                {t('email_verified_success')} ({email})
              </p>
              <Link to="/login" className="btn-primary inline-block" data-testid="go-to-login-btn">
                {t('go_to_login')}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle size={64} className="mx-auto text-red-500 mb-6" />
              <h2 className="font-heading text-2xl text-white mb-2">
                {t('verification_failed')}
              </h2>
              <p className="text-[#A8A29E] mb-2">
                {error || t('verification_failed')}
              </p>
              <p className="text-[#57534E] text-sm mb-6">
                {t('link_expired')}
              </p>
              <div className="space-y-3">
                <Link to="/login" className="btn-primary block" data-testid="go-to-login-error-btn">
                  {t('go_to_login')}
                </Link>
                <Link to="/" className="btn-secondary block" data-testid="back-to-home-btn">
                  {t('back_to_home')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
